import { useState } from 'react';
import { Presentation, FileText, LayoutGrid, Loader2 } from 'lucide-react';
import { generateGammaDocument, downloadFromUrl, getFormatLabel, GammaFormat } from '@/services/gammaService';
import { downloadResumenAsWord } from '@/services/resumenService';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useEffectiveMode } from '@/hooks/useEffectiveMode';
import { saveDownload, DownloadType, DownloadMode, getDownloadModeFromCuerpo } from '@/services/downloadHistoryService';
import { checkOpositorQuota, invalidateOpositorQuotaCache } from '@/services/opositorQuotaService';

interface GammaGenerateButtonsProps {
  content: string;
  userQuestion?: string;
  chatTitle?: string;
}

export function GammaGenerateButtons({ content, userQuestion, chatTitle }: GammaGenerateButtonsProps) {
  const { profile, user } = useAuth();
  const [generatingFormat, setGeneratingFormat] = useState<GammaFormat | null>(null);
  const { toast } = useToast();
  const mode = useEffectiveMode();

  const getDownloadMode = (): DownloadMode => {
    if (typeof mode === 'string' && mode.startsWith('oposiciones') && mode !== 'oposiciones') {
      return mode as DownloadMode;
    }
    if (profile?.opositor_body) {
      return getDownloadModeFromCuerpo(profile.opositor_body);
    }
    if (mode === 'oposiciones') return 'oposiciones';
    return 'funcionarios';
  };

  const isOpositor = typeof mode === 'string' && mode.startsWith('oposiciones');

  const handleGenerate = async (format: GammaFormat) => {
    if (generatingFormat) return;

    // Plan free (opositor): limitar materiales del mes
    if (user && isOpositor) {
      const quota = await checkOpositorQuota(user.id, (profile as any)?.plan, profile, 'materials');
      if (!quota.allowed) {
        toast({
          title: 'Límite diario alcanzado',
          description: `Has generado ${quota.used}/${quota.limit} materiales este mes en el plan gratuito. Vuelve el día 1 o mejora a Plan Opositor.`,
          variant: 'destructive',
        });
        return;
      }
    }

    setGeneratingFormat(format);
    const label = getFormatLabel(format);

    // ===== LOCAL RESUMEN FLOW =====
    if (format === 'resumen') {
      toast({
        title: 'Generando resumen...',
        description: 'Esto puede tardar entre 30 y 45 segundos.',
      });

      try {
        const topicQuery = chatTitle || userQuestion;
        await downloadResumenAsWord({ content, topicQuery, userQuestion });
        if (user) {
          saveDownload({ userId: user.id, title: 'Resumen', downloadType: 'resumen', mode: getDownloadMode(), metadata: { content } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: 'Resumen generado ✓',
          description: 'El archivo Word se ha descargado correctamente.',
        });
      } catch (error: any) {
        console.error('Error generating resumen:', error);
        toast({
          title: 'Error al generar resumen',
          description: error.message || 'No se pudo generar el resumen',
          variant: 'destructive',
        });
      } finally {
        setGeneratingFormat(null);
      }
      return;
    }

    // ===== GAMMA FLOW (presentation & infografia) =====
    toast({
      title: `Generando ${label}...`,
      description: 'Esto puede tardar entre 15 y 45 segundos.',
    });

    try {
      const result = await generateGammaDocument({ 
        content, 
        format,
        examType: profile?.opositor_body || 'oposiciones',
        autonomousCommunity: profile?.autonomous_community || 'extremadura',
        topicQuery: chatTitle || userQuestion,
        userQuestion,
      });

      if (result.isLocal && result.markdown) {
        await navigator.clipboard.writeText(result.markdown);
        if (user) {
          const dlType: DownloadType = format === 'infografia' ? 'infografia' : 'slides';
          await saveDownload({
            userId: user.id,
            title: label,
            downloadType: dlType,
            mode: getDownloadMode(),
            metadata: { content: result.markdown, local: true },
          });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: 'Contenido estructurado ✓',
          description: result.message || 'El texto se ha copiado al portapapeles para que puedas usarlo.',
        });
      } else if (result.exportUrl) {
        downloadFromUrl(result.exportUrl, `${label}.pdf`);
        const dlType: DownloadType = format === 'infografia' ? 'infografia' : 'slides';
        if (user) {
          await saveDownload({ userId: user.id, title: label, downloadType: dlType, mode: getDownloadMode(), fileUrl: result.exportUrl, metadata: { export_url: result.exportUrl, gamma_url: result.gammaUrl } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: `${label} generada`,
          description: result.credits
            ? `PDF descargado. Créditos restantes: ${result.credits.remaining}`
            : 'El PDF se ha descargado correctamente.',
        });
      } else if (result.gammaUrl) {
        window.open(result.gammaUrl, '_blank');
        const dlType: DownloadType = format === 'infografia' ? 'infografia' : 'slides';
        if (user) {
          await saveDownload({ userId: user.id, title: label, downloadType: dlType, mode: getDownloadMode(), fileUrl: result.gammaUrl, metadata: { gamma_url: result.gammaUrl } });
          invalidateOpositorQuotaCache();
        }
        toast({
          title: `${label} generada`,
          description: 'Se ha abierto en Gamma para descargar.',
        });
      }
    } catch (error: any) {
      console.error('Error generating gamma document:', error);
      toast({
        title: 'Error al generar',
        description: error.message || `No se pudo generar la ${label.toLowerCase()}`,
        variant: 'destructive',
      });
    } finally {
      setGeneratingFormat(null);
    }
  };

  const buttons = [
    { format: 'presentation' as GammaFormat, icon: Presentation, label: 'Presentación' },
    { format: 'resumen' as GammaFormat, icon: FileText, label: 'Resumen' },
    { format: 'infografia' as GammaFormat, icon: LayoutGrid, label: 'Infografía' },
  ];

  return (
    <>
      <div className="w-px h-4 bg-border mx-1" />
      {buttons.map(({ format, icon: Icon, label }) => (
        <button
          key={format}
          onClick={() => handleGenerate(format)}
          disabled={generatingFormat !== null}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
            generatingFormat === format
              ? 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-900/30'
              : 'text-muted-foreground hover:text-violet-600 hover:bg-violet-50 dark:hover:text-violet-400 dark:hover:bg-violet-900/30'
          } ${generatingFormat !== null && generatingFormat !== format ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={generatingFormat === format ? `Generando ${label}...` : `Generar ${label}`}
        >
          {generatingFormat === format ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Icon className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">{generatingFormat === format ? 'Generando...' : label}</span>
        </button>
      ))}
    </>
  );
}
